import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Clock, Target, TrendingUp, ChevronRight, BookOpen, Gamepad2, FileText, Brain } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default MedStudyPro Logo Component - Updated geometric M design
const DefaultLogo = ({ dark = true }) => (
  <div className="flex items-center gap-2">
    <svg viewBox="0 0 40 40" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Left M leg */}
      <path d="M4 32V10L12 18L20 10" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Right M leg */}
      <path d="M20 10L28 18L36 10V32" stroke="#7DD3FC" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Center cube - top face */}
      <path d="M20 16L24 18V22L20 24L16 22V18L20 16Z" fill="#334155"/>
      {/* Center cube - visible faces */}
      <path d="M20 16L24 18L20 20L16 18L20 16Z" fill="#475569"/>
      <path d="M20 20L24 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.6"/>
      <path d="M20 20L16 18V22L20 24V20Z" fill="#7DD3FC" fillOpacity="0.4"/>
    </svg>
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Logo with uploaded image + text brand name
const BrandLogo = ({ logoUrl, dark = true }) => (
  <div className="flex items-center gap-2">
    <img 
      src={logoUrl} 
      alt="MedStudyPro" 
      className="h-8 w-8 object-contain"
    />
    <span className={`text-xl font-bold ${dark ? 'text-white' : 'text-[#1E3A5C]'}`}>MedStudyPro</span>
  </div>
);

// Helper to get full URL
const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Handle /uploads/ paths which need /api prefix
  if (url.startsWith('/uploads')) return `${API}/api${url}`;
  if (url.startsWith('/')) return `${API}${url}`;
  return `${API}/${url}`;
};

export default function Landing() {
  const navigate = useNavigate();
  const [siteSettings, setSiteSettings] = useState({
    logo_url: null,
    hero_image_url: null
  });
  
  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      // Fetch logo and hero settings in parallel
      const [logoRes, heroRes] = await Promise.all([
        fetch(`${API}/api/logo-settings`).catch(() => null),
        fetch(`${API}/api/hero-settings`).catch(() => null)
      ]);
      
      const settings = {
        logo_url: null,
        hero_image_url: null
      };
      
      if (logoRes?.ok) {
        const logoData = await logoRes.json();
        settings.logo_url = logoData.logo_url;
      }
      
      if (heroRes?.ok) {
        const heroData = await heroRes.json();
        settings.hero_image_url = heroData.hero_image_url;
      }
      
      setSiteSettings(settings);
    } catch (err) {
      console.error('Failed to fetch site settings:', err);
    }
  };
  
  const handleLogin = () => {
    navigate('/auth');
  };

  const features = [
    {
      step: 1,
      icon: <Target className="w-6 h-6 text-[#FF9E00]" />,
      title: "Choose Your Topic",
      description: "Select from clinical topics like Shock, Hemodynamics, Ventilators, and more."
    },
    {
      step: 2,
      icon: <BookOpen className="w-6 h-6 text-[#FF9E00]" />,
      title: "Practice with Purpose",
      description: "Take adaptive quizzes designed by ICU clinicians to build clinical reasoning."
    },
    {
      step: 3,
      icon: <Brain className="w-6 h-6 text-[#FF9E00]" />,
      title: "Learn from Rationales",
      description: "Understand why answers are correct with evidence-based clinical explanations."
    },
    {
      step: 4,
      icon: <TrendingUp className="w-6 h-6 text-[#FF9E00]" />,
      title: "Track Your Progress",
      description: "Identify weak areas and focus your study where it matters most."
    }
  ];

  // Default hero image if none uploaded
  const defaultHeroImage = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop";
  const heroImage = siteSettings.hero_image_url ? getFullUrl(siteSettings.hero_image_url) : defaultHeroImage;

  return (
    <div className="min-h-screen bg-[#192A3F]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        {siteSettings.logo_url ? (
          <BrandLogo logoUrl={getFullUrl(siteSettings.logo_url)} dark={true} />
        ) : (
          <DefaultLogo dark={true} />
        )}
        <div className="flex items-center gap-6">
          <button 
            onClick={() => navigate('/about')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
            data-testid="nav-about-btn"
          >
            About
          </button>
          <button 
            onClick={() => navigate('/products')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
            data-testid="nav-products-btn"
          >
            Shop
          </button>
          <button 
            onClick={() => navigate('/pricing')}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors"
            data-testid="nav-pricing-btn"
          >
            Pricing
          </button>
          <button 
            onClick={handleLogin}
            className="text-white/80 hover:text-white text-sm font-medium transition-colors flex items-center gap-1"
          >
            <Clock className="w-4 h-4" />
            Login
          </button>
          <Button
            data-testid="landing-get-started-button"
            onClick={handleLogin}
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white px-6 py-2 rounded-full font-semibold text-sm transition-all"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mb-6">
              Test Your <span className="text-[#FF9E00]">Clinical Reasoning</span>—Not Just Your Memory
            </h1>
            <p className="text-lg text-white/70 mb-8 leading-relaxed max-w-xl">
              Adaptive quizzes, case scenarios, and clinical challenges designed to build decision-making confidence for critical care and acute care clinicians.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                data-testid="landing-login-button"
                onClick={handleLogin}
                className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-14 px-8 rounded-full font-semibold text-base transition-all shadow-lg shadow-[#FF9E00]/20"
              >
                Start a Study Session
              </Button>
              <Button
                data-testid="browse-topics-button"
                onClick={() => navigate('/topics')}
                className="bg-[#264B73] hover:bg-[#1E3A5C] text-white h-14 px-8 rounded-full font-semibold text-base transition-all flex items-center gap-2"
              >
                Browse Topics
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Right Content - Clean Hero Image (no floating cards) */}
          <div className="relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={heroImage}
                alt="Healthcare professional with digital technology"
                className="w-full h-[500px] object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#192A3F]/60 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* How MedStudyPro Works Section */}
      <section className="bg-gradient-to-b from-[#192A3F] to-[#0D1520] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              How MedStudyPro Works
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              A smarter way to master clinical reasoning through active practice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Box 1: Pick a Topic */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute -top-2 -left-2 w-10 h-10 bg-[#FF9E00] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                1
              </div>
              
              {/* Visual Mockup - Topic Selection */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 mt-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                    <div className="w-4 h-4 rounded-full border-2 border-[#FF9E00] flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[#FF9E00]"></div>
                    </div>
                    <span className="text-sm text-slate-700">Shock</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300"></div>
                    <span className="text-sm text-slate-500">Hemodynamics</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-slate-200">
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300"></div>
                    <span className="text-sm text-slate-500">Ventilators</span>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-[#1E3A5C] mb-2">Pick a Topic</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Start with areas like Shock, Hemodynamics, or Ventilators.
              </p>
            </div>

            {/* Box 2: Take a Quiz or Game */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute -top-2 -left-2 w-10 h-10 bg-[#FF9E00] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                2
              </div>
              
              {/* Visual Mockup - Quiz Progress */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 mt-4">
                <div className="text-center">
                  <div className="relative w-16 h-16 mx-auto mb-2">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle cx="32" cy="32" r="28" stroke="#E2E8F0" strokeWidth="6" fill="none" />
                      <circle cx="32" cy="32" r="28" stroke="#22C55E" strokeWidth="6" fill="none" strokeDasharray="176" strokeDashoffset="84" strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold text-slate-700">52%</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Practice Progress</p>
                  <p className="text-xs text-green-600 font-medium">Needs Review</p>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-[#1E3A5C] mb-2">Take a Quiz or Game</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Practice with adaptive quizzes designed by ICU clinicians.
              </p>
            </div>

            {/* Box 3: Review Detailed Rationale */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute -top-2 -left-2 w-10 h-10 bg-[#FF9E00] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                3
              </div>
              
              {/* Visual Mockup - Rationale Review */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 mt-4">
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-slate-500 uppercase">Rationale</div>
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <p className="text-xs text-slate-600 line-clamp-3">
                      The correct answer is B. In septic shock, the initial vasopressor of choice is norepinephrine due to its...
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-xs text-green-600">Evidence-based</span>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-[#1E3A5C] mb-2">Review Detailed Rationale</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Understand why answers are correct with clinical explanations.
              </p>
            </div>

            {/* Box 4: Identify Weak Spots */}
            <div className="bg-white rounded-2xl p-6 relative overflow-hidden group hover:shadow-xl transition-shadow">
              <div className="absolute -top-2 -left-2 w-10 h-10 bg-[#FF9E00] rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                4
              </div>
              
              {/* Visual Mockup - Performance Dashboard */}
              <div className="bg-slate-50 rounded-xl p-4 mb-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-600">4.0</div>
                    <div className="text-xs text-slate-500">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-[#FF9E00]">60%</div>
                    <div className="text-xs text-slate-500">Mastery</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">88%</div>
                    <div className="text-xs text-slate-500">Streak</div>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-slate-600">Adaptive learning</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-slate-600">Focus on weak areas</span>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-bold text-[#1E3A5C] mb-2">Identify Weak Spots</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Track your progress and focus on areas that need improvement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1E3A5C] py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Sharpen Your Clinical Skills?
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Join healthcare professionals who are mastering critical care with MedStudyPro.
          </p>
          <Button
            onClick={handleLogin}
            className="bg-[#FF9E00] hover:bg-[#E88D00] text-white h-14 px-10 rounded-full font-semibold text-base transition-all shadow-lg shadow-[#FF9E00]/20"
          >
            Start Your Free Trial
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0D1520] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {siteSettings.logo_url ? (
              <BrandLogo logoUrl={getFullUrl(siteSettings.logo_url)} dark={true} />
            ) : (
              <DefaultLogo dark={true} />
            )}
            <div className="flex items-center gap-8">
              <button onClick={() => navigate('/')} className="text-white/60 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => navigate('/about')} className="text-white/60 hover:text-white text-sm transition-colors">About</button>
              <button onClick={() => navigate('/pricing')} className="text-white/60 hover:text-white text-sm transition-colors">Pricing</button>
              <button onClick={() => navigate('/contact')} className="text-white/60 hover:text-white text-sm transition-colors">Contact</button>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© 2026 MedStudyPro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
